import { AmazonianHardwareWithTaxonomyID } from "@amzn/amazonian-hardware-catalogical";
import { IItemVersion, ItemDetails, ItemTypes } from "@/common/types/item.types";
import { useHardwareDetails } from "@/hooks/use-hardware-details";
import { useHardwareCatalog, CatalogItemsParams } from "@/hooks/use-hardware-catalog";
import { useMemo, useState } from "react";

export interface UseGetHardwareDetailsResult {
  type: ItemTypes;
  item: ItemDetails | undefined;
  isLoading: boolean;
  error: Error | undefined;
  defaultVersion: IItemVersion | undefined;
  versions: IItemVersion[];
  updateVersionSelected: (version: IItemVersion) => void;
  getItem: (taxonomyId: string) => void;
}

export function useGetHardwareDetails(): UseGetHardwareDetailsResult {
  const [taxonomyId, setTaxonomyId] = useState<string | undefined>(undefined);
  const [selectedChildId, setSelectedChildId] = useState<string | undefined>(undefined);

  const { data: parentItem, error: parentError, isLoading: parentLoading } = useHardwareDetails(taxonomyId);

  const childParams = useMemo<CatalogItemsParams | undefined>(
    () =>
      taxonomyId
        ? {
            limit: 500,
            offset: 0,
            sortBy: "name",
            sortDirection: "asc",
            filter: JSON.stringify({ parentTaxonomyId: taxonomyId }),
          }
        : undefined,
    [taxonomyId]
  );

  const { data: childItems, error: childError, isLoading: childLoading } = useHardwareCatalog(childParams);

  const hasChildren = childItems && childItems.length > 0;
  const selectedChild = hasChildren ? childItems.find((c) => c.taxonomyId === selectedChildId) : undefined;

  const mappedItem = useMemo(() => {
    if (!parentItem) return undefined;
    const source = selectedChild ?? parentItem;
    return mapItem(source, parentItem);
  }, [parentItem, selectedChild]);

  const versions = useMemo(() => {
    if (!parentItem || !hasChildren) return [];
    return childItems.map((child) => mapVersion(child, parentItem));
  }, [parentItem, childItems]);

  const defaultVersion = useMemo(
    () => resolveDefaultVersion(parentItem, selectedChild, hasChildren),
    [parentItem, selectedChild, hasChildren]
  );

  const updateVersionSelected = (version: IItemVersion) => {
    setSelectedChildId(version.taxonomyId);
  };

  const getItem = (id: string) => {
    setSelectedChildId(undefined);
    setTaxonomyId(id);
  };

  return {
    type: ItemTypes.HARDWARE,
    item: mappedItem,
    isLoading: parentLoading || (!!taxonomyId && childLoading),
    error: parentError ?? childError,
    defaultVersion,
    versions,
    updateVersionSelected,
    getItem,
  };
}

const resolveDefaultVersion = (
  parentItem: AmazonianHardwareWithTaxonomyID | undefined,
  selectedChild: AmazonianHardwareWithTaxonomyID | undefined,
  hasChildren: boolean | undefined
): IItemVersion | undefined => {
  if (!parentItem) return undefined;
  if (selectedChild) return mapVersion(selectedChild, parentItem);
  if (!hasChildren) return mapVersion(parentItem, parentItem);
  return undefined;
};

function fallback<T>(value: T | undefined | null, parentValue: T | undefined | null, defaultValue: T): T {
  if (value !== undefined && value !== null && value !== "") return value;
  if (parentValue !== undefined && parentValue !== null && parentValue !== "") return parentValue;
  return defaultValue;
}

function mapItem(item: AmazonianHardwareWithTaxonomyID, parent: AmazonianHardwareWithTaxonomyID): ItemDetails {
  return {
    taxonomyId: item.taxonomyId,
    parentTxId: parent.taxonomyId,
    parentName: parent.name,
    name: fallback(item.name, parent.name, ""),
    description: fallback(item.description, parent.description, ""),
    shortDescription: fallback(item.shortDescription, parent.shortDescription, ""),
    status: fallback(item.status, parent.status, ""),
    logoUrl: fallback(item.logoUrl, parent.logoUrl, ""),
    publisher: fallback(item.manufacturer, parent.manufacturer, ""),
    etaDays: fallback(item.etaDays, parent.etaDays, 0),
    priceAmount: fallback(item.price?.amount, parent.price?.amount, 0),
    priceCurrency: fallback(item.price?.currency, parent.price?.currency, ""),
    os: item.os ? [item.os] : parent.os ? [parent.os] : ["-"],
    categories: [fallback(item.subCategory, parent.subCategory, "-")],
    hasStock: true,
    version: fallback(item.model, parent.model, ""),
    dataClassification: "N/A",
    securityStatus: "N/A",
  };
}

function mapVersion(item: AmazonianHardwareWithTaxonomyID, parent: AmazonianHardwareWithTaxonomyID): IItemVersion {
  return {
    taxonomyId: item.taxonomyId,
    name: fallback(item.name, parent.name, ""),
    displayText: item.model,
    status: fallback(item.status, parent.status, ""),
    amount: fallback(item.price?.amount, parent.price?.amount, 0),
    currency: fallback(item.price?.currency, parent.price?.currency, ""),
    logoUrl: fallback(item.logoUrl, parent.logoUrl, ""),
  };
}
